import { useEffect, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Reveal } from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLEET_RANGES = ['1–49', '50–99', '100–249', '250–499', '500–999', '1,000–2,499', '2,500–4,999', '5,000+'];
const REGIONS = ['United Kingdom', 'Ireland', 'Europe', 'North America', 'Middle East', 'Africa', 'Asia-Pacific', 'Multiple regions / International', 'Other'];
const ORG_TYPES = [
  'Fleet management company',
  'Leasing / vehicle management provider',
  'Company operating its own fleet',
  'Public sector / local authority',
  'Housing / property organisation',
  'Logistics / transport',
  'Service provider / supplier',
  'Other',
];
const INTERESTS = [
  'Fleet operations platform',
  'Driver experience',
  'Fleet manager workspace',
  'Account/service team operations',
  'Operational workflow automation',
  'White-labelled/client-specific implementation',
  'Integrations',
  'Other',
];

const inputCls =
  'w-full rounded-lg border border-[#D1D5DB] bg-white px-3.5 py-2.5 text-sm text-[#111111] placeholder-[#9CA3AF] transition-colors duration-150 focus:border-[#0F5C4A] focus:outline-none focus:ring-2 focus:ring-[#0F5C4A]/20';
const labelCls = 'mb-1.5 block text-sm font-medium text-[#111111]';
const errCls = 'mt-1.5 flex items-center gap-1 text-xs font-medium text-[#B3261E]';

const Field = ({ id, label, required, error, children }) => (
  <div>
    <label htmlFor={id} className={labelCls}>
      {label} {required && <span className="text-[#B3261E]" aria-hidden="true">*</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} role="alert" className={errCls} data-testid={`${id}-error`}>
        <AlertTriangle size={12} /> {error}
      </p>
    )}
  </div>
);

const empty = {
  full_name: '', work_email: '', company: '', job_title: '', fleet_parc: '', region: '', org_type: '',
  interests: [], current_systems: '', improve: '', preferred_contact: 'email', phone: '', website: '',
};

export const EnquiryForm = () => {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState('idle');
  const startedAt = useRef(0);

  useEffect(() => {
    startedAt.current = Date.now();
  }, []);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const toggleInterest = (value) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(value) ? f.interests.filter((i) => i !== value) : [...f.interests, value],
    }));
  };

  const validate = () => {
    const er = {};
    if (form.full_name.trim().length < 2) er.full_name = 'Please enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.work_email.trim())) er.work_email = 'Please enter a valid work email.';
    if (form.company.trim().length < 2) er.company = 'Please enter your company.';
    if (!form.fleet_parc) er.fleet_parc = 'Please select your fleet size.';
    if (!form.region) er.region = 'Please select your region.';
    if (!form.org_type) er.org_type = 'Please select your organisation type.';
    if (form.improve.trim().length < 10) er.improve = 'Please tell us a little more (at least 10 characters).';
    if (form.preferred_contact === 'phone' && form.phone.trim().length < 6)
      er.phone = 'Please enter a telephone number we can call.';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (state === 'submitting' || state === 'success') return;
    if (!validate()) {
      setState('idle');
      return;
    }
    setState('submitting');
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, form_started_at: startedAt.current }),
      });
      if (!res.ok) throw new Error('Request failed');
      setState('success');
    } catch {
      setState('error');
    }
  };

  return (
    <section id="enquiry" className="scroll-mt-20 border-t border-[#E5E7EB] bg-[#F4F5F1] py-20 md:py-28" data-testid="enquiry-section">
      <div className="container-relay grid grid-cols-1 gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow mb-4" data-testid="enquiry-eyebrow">Enquiry</p>
          <h2 className="text-3xl font-bold tracking-tight text-[#111111] sm:text-4xl" data-testid="enquiry-headline">
            Request a conversation.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#6B7280]">
            Tell us about your fleet and what you&rsquo;d like to improve. A member of the AVENIR team will respond
            personally — no automated sequences.
          </p>
          <p className="mt-6 text-sm text-[#6B7280]">
            Prefer email?{' '}
            <a href="mailto:hello@relaybyavenir.com" className="font-semibold text-[#0F5C4A] underline-offset-4 hover:underline" data-testid="enquiry-email-link">
              hello@relaybyavenir.com
            </a>
          </p>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-8">
          <div className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-[0_4px_24px_rgba(16,24,40,0.05)] sm:p-9">
            {state === 'success' ? (
              <div className="flex flex-col items-start gap-4 py-10" data-testid="enquiry-success" role="status">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E4EFE9]">
                  <CheckCircle2 size={24} className="text-[#0F5C4A]" />
                </span>
                <h3 className="text-2xl font-bold tracking-tight text-[#111111]">
                  Thanks — we&rsquo;ve received your enquiry.
                </h3>
                <p className="text-base text-[#6B7280]">We&rsquo;ll be in touch.</p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="enquiry-form">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field id="full-name" label="Full name" required error={errors.full_name}>
                    <input id="full-name" data-testid="enquiry-full-name" className={inputCls} value={form.full_name} onChange={set('full_name')} autoComplete="name" aria-describedby={errors.full_name ? 'full-name-error' : undefined} />
                  </Field>
                  <Field id="work-email" label="Work email" required error={errors.work_email}>
                    <input id="work-email" type="email" data-testid="enquiry-work-email" className={inputCls} value={form.work_email} onChange={set('work_email')} autoComplete="email" aria-describedby={errors.work_email ? 'work-email-error' : undefined} />
                  </Field>
                  <Field id="company" label="Company" required error={errors.company}>
                    <input id="company" data-testid="enquiry-company" className={inputCls} value={form.company} onChange={set('company')} autoComplete="organization" aria-describedby={errors.company ? 'company-error' : undefined} />
                  </Field>
                  <Field id="job-title" label="Job title / role" error={errors.job_title}>
                    <input id="job-title" data-testid="enquiry-job-title" className={inputCls} value={form.job_title} onChange={set('job_title')} autoComplete="organization-title" />
                  </Field>
                  <Field id="fleet-parc" label="Fleet parc / number of vehicles" required error={errors.fleet_parc}>
                    <select id="fleet-parc" data-testid="enquiry-fleet-parc" className={inputCls} value={form.fleet_parc} onChange={set('fleet_parc')} aria-describedby={errors.fleet_parc ? 'fleet-parc-error' : undefined}>
                      <option value="">Select a range</option>
                      {FLEET_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </Field>
                  <Field id="region" label="Region / primary operating geography" required error={errors.region}>
                    <select id="region" data-testid="enquiry-region" className={inputCls} value={form.region} onChange={set('region')} aria-describedby={errors.region ? 'region-error' : undefined}>
                      <option value="">Select a region</option>
                      {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field id="org-type" label="Organisation type" required error={errors.org_type}>
                      <select id="org-type" data-testid="enquiry-org-type" className={inputCls} value={form.org_type} onChange={set('org_type')} aria-describedby={errors.org_type ? 'org-type-error' : undefined}>
                        <option value="">Select organisation type</option>
                        {ORG_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                  </div>
                  <fieldset className="sm:col-span-2">
                    <legend className={labelCls}>What are you interested in?</legend>
                    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-testid="enquiry-interests">
                      {INTERESTS.map((i) => (
                        <label key={i} className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-[#E5E7EB] px-3 py-2.5 text-sm text-[#374151] transition-colors duration-150 hover:border-[#0F5C4A] has-[:checked]:border-[#0F5C4A] has-[:checked]:bg-[#E4EFE9]/50">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-[#D1D5DB] accent-[#0F5C4A]"
                            checked={form.interests.includes(i)}
                            onChange={() => toggleInterest(i)}
                            data-testid={`interest-${i.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          />
                          {i}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div className="sm:col-span-2">
                    <Field id="current-systems" label="Current systems / platforms" error={errors.current_systems}>
                      <input id="current-systems" data-testid="enquiry-current-systems" className={inputCls} value={form.current_systems} onChange={set('current_systems')} placeholder="Optional — e.g. fleet, maintenance or comms tools you already use" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field id="improve" label="What would you like Relay to help improve?" required error={errors.improve}>
                      <textarea id="improve" rows={4} data-testid="enquiry-improve" className={inputCls} value={form.improve} onChange={set('improve')} aria-describedby={errors.improve ? 'improve-error' : undefined} />
                    </Field>
                  </div>
                  <fieldset className="sm:col-span-2">
                    <legend className={labelCls}>Preferred contact method</legend>
                    <div className="flex gap-3" data-testid="enquiry-preferred-contact">
                      {['email', 'phone'].map((m) => (
                        <label key={m} className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-[#E5E7EB] px-4 py-2.5 text-sm capitalize text-[#374151] has-[:checked]:border-[#0F5C4A] has-[:checked]:bg-[#E4EFE9]/50">
                          <input
                            type="radio"
                            name="preferred-contact"
                            value={m}
                            className="h-4 w-4 accent-[#0F5C4A]"
                            checked={form.preferred_contact === m}
                            onChange={() => setForm((f) => ({ ...f, preferred_contact: m }))}
                            data-testid={`contact-${m}`}
                          />
                          {m}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  {form.preferred_contact === 'phone' && (
                    <div className="sm:col-span-2">
                      <Field id="phone" label="Telephone number" required error={errors.phone}>
                        <input id="phone" type="tel" data-testid="enquiry-phone" className={inputCls} value={form.phone} onChange={set('phone')} autoComplete="tel" aria-describedby={errors.phone ? 'phone-error' : undefined} />
                      </Field>
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={set('website')}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0"
                />
                {state === 'error' && (
                  <div role="alert" data-testid="enquiry-error" className="mt-5 flex items-start gap-2.5 rounded-lg border border-[#F0C7C2] bg-[#FBEDEB] px-4 py-3 text-sm text-[#B3261E]">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <span>
                      Something went wrong sending your enquiry. Please try again, or email us directly at{' '}
                      <a href="mailto:hello@relaybyavenir.com" className="font-semibold underline">hello@relaybyavenir.com</a>.
                    </span>
                  </div>
                )}
                <button type="submit" disabled={state === 'submitting'} data-testid="enquiry-submit" className="btn-primary mt-7 w-full">
                  {state === 'submitting' ? 'Sending…' : 'Request a conversation'} <ArrowRight size={16} />
                </button>
                <p className="mt-4 text-center text-xs text-[#6B7280]" data-testid="enquiry-privacy-note">
                  We&rsquo;ll use these details only to respond to your enquiry. See our{' '}
                  <a href="/privacy" className="underline underline-offset-2 hover:text-[#0F5C4A]" data-testid="enquiry-privacy-link">Privacy Policy</a>.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
